
import React from 'react';
import { Link } from 'react-router-dom';

const HomeMedicalCarePharmacistPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      {/* ヘッダー */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2">
              <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                <span className="text-white font-bold text-lg">B</span>
              </div>
              <span className="text-xl font-bold text-gray-800" style={{ fontFamily: '"Pacifico", serif' }}>Beast Pharmacy</span>
            </Link>
            <nav className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-600 hover:text-orange-500 transition-colors">ホーム</Link>
              <Link to="/services" className="text-gray-600 hover:text-orange-500 transition-colors">サービス</Link>
              <Link to="/beast-blog" className="text-orange-500 font-medium">ビースト君ブログ</Link>
              <Link to="/health-consultation" className="text-gray-600 hover:text-orange-500 transition-colors">健康相談</Link>
            </nav>
          </div>
        </div>
      </header>

      {/* メインコンテンツ */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* パンくずナビ */}
        <nav className="mb-8">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-orange-500">ホーム</Link>
            <span>/</span>
            <Link to="/beast-blog" className="hover:text-orange-500">ビースト君ブログ</Link>
            <span>/</span>
            <span className="text-gray-800">在宅医療における薬剤師の役割と支援内容をわかりやすく紹介</span>
          </div>
        </nav>

        {/* 記事ヘッダー */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <span className="bg-orange-100 text-orange-600 px-3 py-1 rounded-full text-sm font-medium">在宅医療</span>
            <span className="text-gray-500 text-sm">2025年10月25日</span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4 leading-tight">
            在宅医療における薬剤師の役割と支援内容をわかりやすく紹介
          </h1>
          <p className="text-lg text-gray-600 leading-relaxed">
            高齢化社会の進展とともに、在宅医療の重要性が高まっています。その中で薬剤師が果たす役割は非常に大きく、患者さんの生活の質向上に欠かせない存在となっています。
          </p>
        </div>

        {/* メイン画像 */}
        <div className="mb-8">
          <img 
            src="https://public.readdy.ai/ai/img_res/53f8be320eb5e32381ff74f7e561e5c7.jpg"
            alt="在宅医療における薬剤師の役割"
            className="w-full h-64 md:h-80 object-cover rounded-lg shadow-lg"
          />
        </div>

        {/* 記事本文 */}
        <article className="prose prose-lg max-w-none">
          {/* 導入部 */}
          <div className="bg-orange-50 border-l-4 border-orange-500 p-6 mb-8 rounded-r-lg">
            <h2 className="text-xl font-bold text-orange-800 mb-3">この記事のポイント</h2>
            <ul className="text-orange-700 space-y-2">
              <li>• 在宅医療における薬剤師の具体的な役割</li>
              <li>• 訪問薬剤師の仕事内容と専門性</li>
              <li>• 患者さんとご家族が受けられる支援内容</li>
              <li>• 在宅医療チームでの薬剤師の位置づけ</li>
            </ul>
          </div>

          {/* 在宅医療とは */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">在宅医療とは</h2>
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <p className="text-gray-700 leading-relaxed mb-4">
                在宅医療とは、病院や診療所ではなく、患者さんのご自宅で医療サービスを提供することです。高齢化や医療技術の進歩により、自宅で安心して療養できる環境が整ってきています。
              </p>
              <p className="text-gray-700 leading-relaxed">
                この在宅医療において、薬剤師は薬物療法の専門家として、患者さんの安全で効果的な薬物治療をサポートする重要な役割を担っています。
              </p>
            </div>
          </section>

          {/* 在宅医療における薬剤師の役割 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">在宅医療における薬剤師の役割</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-medicine-bottle-line text-orange-600 text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">薬物療法の管理</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  処方薬の適切な管理、服薬指導、副作用のモニタリングを行い、安全で効果的な薬物治療をサポートします。
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-user-heart-line text-orange-600 text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">患者・家族支援</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  患者さんとご家族に対する服薬指導、薬に関する相談対応、不安の解消などを通じて療養生活をサポートします。
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-team-line text-orange-600 text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">多職種連携</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  医師、看護師、ケアマネジャーなどの医療・介護チームと連携し、総合的な在宅医療を提供します。
                </p>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mr-4">
                    <i className="ri-shield-check-line text-orange-600 text-xl"></i>
                  </div>
                  <h3 className="text-lg font-bold text-gray-800">安全性の確保</h3>
                </div>
                <p className="text-gray-600 leading-relaxed">
                  薬の相互作用チェック、重複投薬の防止、適切な保管方法の指導など、薬物治療の安全性を確保します。
                </p>
              </div>
            </div>
          </section>

          {/* 訪問薬剤師の具体的な仕事内容 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">訪問薬剤師の具体的な仕事内容</h2>
            
            <div className="space-y-6">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-blue-800 mb-3">
                  <i className="ri-home-heart-line mr-2"></i>
                  訪問による服薬指導
                </h3>
                <ul className="text-blue-700 space-y-2 ml-6">
                  <li>• 患者さんのご自宅を訪問し、薬の正しい飲み方を指導</li>
                  <li>• 薬の効果や副作用について詳しく説明</li>
                  <li>• 服薬状況の確認と改善提案</li>
                  <li>• 薬の保管方法や管理方法の指導</li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-green-800 mb-3">
                  <i className="ri-stethoscope-line mr-2"></i>
                  健康状態のモニタリング
                </h3>
                <ul className="text-green-700 space-y-2 ml-6">
                  <li>• 薬の効果や副作用の確認</li>
                  <li>• 体調変化の観察と記録</li>
                  <li>• 血圧、血糖値などの測定サポート</li>
                  <li>• 必要に応じて医師への報告と提案</li>
                </ul>
              </div>

              <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
                <h3 className="text-lg font-bold text-purple-800 mb-3">
                  <i className="ri-capsule-line mr-2"></i>
                  薬剤管理と調剤
                </h3>
                <ul className="text-purple-700 space-y-2 ml-6">
                  <li>• 一包化調剤による服薬管理の簡素化</li>
                  <li>• 残薬整理と無駄な薬の削減</li>
                  <li>• 薬の相互作用チェック</li>
                  <li>• 医療機器の使用方法指導</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 患者・家族への支援内容 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">患者さん・ご家族への支援内容</h2>
            
            <div className="bg-gradient-to-r from-orange-50 to-yellow-50 p-8 rounded-lg mb-6">
              <h3 className="text-xl font-bold text-orange-800 mb-6 text-center">薬剤師が提供する包括的サポート</h3>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-question-answer-line text-orange-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">相談対応</h4>
                  <p className="text-sm text-gray-600">薬に関する疑問や不安に24時間対応でお答えします</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-book-open-line text-orange-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">教育・指導</h4>
                  <p className="text-sm text-gray-600">病気や薬について分かりやすく説明し、理解を深めます</p>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-orange-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-heart-pulse-line text-orange-600 text-2xl"></i>
                  </div>
                  <h4 className="font-bold text-gray-800 mb-2">生活支援</h4>
                  <p className="text-sm text-gray-600">日常生活での薬の管理方法をサポートします</p>
                </div>
              </div>
            </div>
          </section>

          {/* 在宅医療チームでの連携 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">在宅医療チームでの連携</h2>
            
            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <img 
                src="https://readdy.ai/api/search-image?query=Healthcare%20team%20collaboration%20in%20home%20medical%20care%2C%20pharmacist%20working%20with%20doctors%20nurses%20and%20care%20managers%2C%20professional%20meeting%20discussion%2C%20Japanese%20medical%20setting%2C%20teamwork%20and%20communication&width=600&height=300&seq=team-collaboration&orientation=landscape"
                alt="在宅医療チームでの連携"
                className="w-full h-48 object-cover rounded-lg mb-4"
              />
              <p className="text-gray-700 leading-relaxed">
                在宅医療では、様々な専門職が連携して患者さんをサポートします。薬剤師は薬物療法の専門家として、医師の治療方針に基づき、看護師やケアマネジャーと情報を共有しながら、最適な薬物治療を提供します。
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">医師との連携</h3>
                <ul className="text-gray-600 space-y-2">
                  <li>• 処方内容の確認と提案</li>
                  <li>• 薬の効果・副作用の報告</li>
                  <li>• 処方変更の相談</li>
                  <li>• 治療方針の共有</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg p-6">
                <h3 className="text-lg font-bold text-gray-800 mb-4">看護師・介護職との連携</h3>
                <ul className="text-gray-600 space-y-2">
                  <li>• 服薬状況の情報共有</li>
                  <li>• 体調変化の報告</li>
                  <li>• ケアプランへの参画</li>
                  <li>• 家族への指導協力</li>
                </ul>
              </div>
            </div>
          </section>

          {/* 在宅医療薬剤師のメリット */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">在宅医療薬剤師を利用するメリット</h2>
            
            <div className="space-y-4">
              <div className="flex items-start space-x-4 bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">通院負担の軽減</h3>
                  <p className="text-gray-600">薬局への通院が困難な患者さんでも、自宅で薬剤師のサービスを受けられます。</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">個別対応の充実</h3>
                  <p className="text-gray-600">患者さん一人ひとりの生活環境に合わせた、きめ細やかな服薬指導が可能です。</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">安全性の向上</h3>
                  <p className="text-gray-600">定期的な訪問により、薬の管理状況を確認し、事故や副作用を未然に防げます。</p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-white border border-gray-200 rounded-lg p-6">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-check-line text-green-600"></i>
                </div>
                <div>
                  <h3 className="font-bold text-gray-800 mb-2">家族の安心</h3>
                  <p className="text-gray-600">ご家族も薬について相談でき、介護負担の軽減につながります。</p>
                </div>
              </div>
            </div>
          </section>

          {/* よくある質問 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">よくある質問</h2>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-800">Q. 在宅医療薬剤師のサービスは保険適用されますか？</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-600">はい、医師の指示に基づく在宅患者訪問薬剤管理指導は健康保険の適用対象です。自己負担額は1割〜3割となります。</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-800">Q. どのような患者さんが対象になりますか？</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-600">通院が困難な患者さん、在宅医療を受けている患者さん、薬の管理に不安がある患者さんなどが対象となります。</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-800">Q. 訪問頻度はどのくらいですか？</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-600">患者さんの状態や薬の種類により異なりますが、月1〜2回程度が一般的です。必要に応じて頻度を調整します。</p>
                </div>
              </div>

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-orange-50 p-4 border-b border-gray-200">
                  <h3 className="font-bold text-gray-800">Q. 緊急時の対応はどうなりますか？</h3>
                </div>
                <div className="p-4">
                  <p className="text-gray-600">24時間対応の相談窓口を設けており、薬に関する緊急の相談にお答えします。必要に応じて医師や看護師と連携します。</p>
                </div>
              </div>
            </div>
          </section>

          {/* まとめ */}
          <section className="mb-10">
            <div className="bg-gradient-to-r from-orange-500 to-yellow-500 text-white p-8 rounded-lg">
              <h2 className="text-2xl font-bold mb-6 text-center">まとめ</h2>
              <div className="space-y-4 text-center">
                <p className="text-lg leading-relaxed">
                  在宅医療における薬剤師は、患者さんの安全で効果的な薬物治療を支える重要な存在です。
                </p>
                <p className="leading-relaxed">
                  訪問薬剤師の専門的なサポートにより、患者さんとご家族が安心して在宅療養を続けることができます。薬に関するお悩みがございましたら、お気軽にご相談ください。
                </p>
              </div>
            </div>
          </section>

          {/* 関連記事 */}
          <section className="mb-10">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b-2 border-orange-200 pb-2">関連記事</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/beast-blog/family-pharmacist-guide" className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=Family%20pharmacist%20consultation%20with%20elderly%20patient%2C%20professional%20healthcare%20advice%2C%20warm%20and%20caring%20atmosphere%2C%20Japanese%20pharmacy%20setting&width=300&height=200&seq=family-pharmacist-related&orientation=landscape"
                  alt="かかりつけ薬剤師とは"
                  className="w-full h-32 object-cover"
                />
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2 text-sm">かかりつけ薬剤師とは？メリットと登録方法を徹底解説</h3>
                  <p className="text-gray-600 text-xs">2025年10月15日</p>
                </div>
              </Link>

              <Link to="/beast-blog/elderly-medication-management" className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=Elderly%20medication%20management%2C%20pill%20organizer%2C%20senior%20healthcare%2C%20medication%20safety%2C%20Japanese%20healthcare%20setting&width=300&height=200&seq=elderly-med-related&orientation=landscape"
                  alt="高齢者の薬物管理"
                  className="w-full h-32 object-cover"
                />
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2 text-sm">高齢者の薬物管理：安全で効果的な服薬のポイント</h3>
                  <p className="text-gray-600 text-xs">2025年9月20日</p>
                </div>
              </Link>

              <Link to="/beast-blog/multidisciplinary-collaboration" className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                <img 
                  src="https://readdy.ai/api/search-image?query=Healthcare%20team%20collaboration%2C%20medical%20professionals%20working%20together%2C%20multidisciplinary%20approach%2C%20Japanese%20medical%20setting&width=300&height=200&seq=collaboration-related&orientation=landscape"
                  alt="多職種連携"
                  className="w-full h-32 object-cover"
                />
                <div className="p-4">
                  <h3 className="font-bold text-gray-800 mb-2 text-sm">多職種連携で実現する質の高い薬物療法</h3>
                  <p className="text-gray-600 text-xs">2025年8月25日</p>
                </div>
              </Link>
            </div>
          </section>
        </article>

        {/* 記事下部のCTA */}
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-8 text-center">
          <h3 className="text-xl font-bold text-orange-800 mb-4">在宅医療についてのご相談</h3>
          <p className="text-orange-700 mb-6">
            在宅医療や訪問薬剤師サービスについて詳しく知りたい方は、お気軽にご相談ください。
          </p>
          <Link 
            to="/health-consultation" 
            className="bg-orange-500 text-white px-8 py-3 rounded-lg font-medium hover:bg-orange-600 transition-colors inline-block whitespace-nowrap"
          >
            健康相談を予約する
          </Link>
        </div>
      </main>

      {/* フッター */}
      <footer className="bg-gray-800 text-white py-12 mt-16">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white font-bold">B</span>
                </div>
                <span className="text-lg font-bold" style={{ fontFamily: '"Pacifico", serif' }}>Beast Pharmacy</span>
              </div>
              <p className="text-gray-400 text-sm">
                地域の皆様の健康を支える薬局として、質の高い薬剤師サービスを提供しています。
              </p>
            </div>
            <div>
              <h4 className="font-bold mb-4">サービス</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/services" className="hover:text-white">調剤薬局</Link></li>
                <li><Link to="/health-consultation" className="hover:text-white">健康相談</Link></li>
                <li><Link to="/home-care" className="hover:text-white">在宅医療</Link></li>
                <li><Link to="/otc" className="hover:text-white">OTC医薬品</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">情報</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><Link to="/beast-blog" className="hover:text-white">ビースト君ブログ</Link></li>
                <li><Link to="/drug-price-search" className="hover:text-white">薬価検索</Link></li>
                <li><Link to="/package-insert-search" className="hover:text-white">添付文書検索</Link></li>
                <li><Link to="/information" className="hover:text-white">お知らせ</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">お問い合わせ</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <p>📞 03-1234-5678</p>
                <p>📧 info@beast-pharmacy.jp</p>
                <p>🕒 平日 9:00-18:00</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 Beast Pharmacy. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomeMedicalCarePharmacistPage;
